import { FC, ReactNode, useRef, useState, type JSX } from 'react';
import { styled } from '@mui/material/styles';
import { Coords, PortalPosition } from '../../types';
import { Portal } from '../Portal/Portal';
import { useMenuState } from './MenuContext';

interface MenuProps {
  children: ReactNode;
  togglerContent: JSX.Element | null;
  renderPortal?: boolean;
  portalStartPosition?: PortalPosition;
  disabled?: boolean;
}

interface PortalWrapperProps {
  cords: Coords;
  position: PortalPosition;
}

const PortalWrapper = styled('div')<PortalWrapperProps>`
  ${({ cords, position }) =>
    position === PortalPosition.right ? `right: ${cords.right}px;` : `left: ${cords.left}px;`}
  top: ${({ cords }) => cords.top}px;
  position: absolute;
`;

const Container = styled('div')``;
const DropdownButton = styled('div')``;
const DropdownMenu = styled('div')``;

export const Menu: FC<MenuProps> = ({
  children,
  togglerContent,
  renderPortal = false,
  portalStartPosition = PortalPosition.left,
  disabled = false,
}) => {
  const ref = useRef<null | HTMLDivElement>(null);
  const portalContentRef = useRef<null | HTMLDivElement>(null);
  const { onToggle, open } = useMenuState();

  const [cords, setCords] = useState<Coords>({ top: 0 });

  const onClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (disabled) {
      return;
    }

    if (renderPortal) {
      if (!ref.current || !children) {
        return;
      }
      const offset = ref.current.getBoundingClientRect();
      const cords: Coords = { top: offset.top + window.document.documentElement.scrollTop };
      if (portalStartPosition === PortalPosition.right) {
        cords.right = window.document.documentElement.offsetWidth - offset.right;
      }
      if (portalStartPosition === PortalPosition.left) {
        cords.left = offset.left;
      }
      setCords(cords);
      onToggle(portalContentRef);
      return;
    }
    onToggle(ref);
  };

  return (
    <Container ref={ref}>
      <DropdownButton onClick={onClick}>{togglerContent}</DropdownButton>
      {!renderPortal && (
        <DropdownMenu role='listbox'>{children && open && <div>{children}</div>}</DropdownMenu>
      )}
      {renderPortal && open && (
        <Portal>
          <PortalWrapper ref={portalContentRef} cords={cords} position={portalStartPosition}>
            {children}
          </PortalWrapper>
        </Portal>
      )}
    </Container>
  );
};
